import React, { useState, useEffect } from "react";
import { apiJson } from "../../utils/api";

const NATIONALITY = ["INDIAN", "OTHER"];
const RELIGION = ["HINDU", "MUSLIM", "CHRISTIAN", "SIKH", "BUDDHIST", "JAIN", "OTHER"];
const GENDER = ["MALE", "FEMALE", "OTHER"];
const CASTE = ["UR", "OBC", "SC", "ST", "BC", "EBC", "EWS", "PH", "URF"];
const SELECT_OPTS = { gender: GENDER, casteCategory: CASTE, nationality: NATIONALITY, religion: RELIGION };

const EDIT_FIELDS = [
  { key: "name",          label: "Name" },
  { key: "fatherName",    label: "Father's Name" },
  { key: "motherName",    label: "Mother's Name" },
  { key: "gender",        label: "Gender" },
  { key: "dateOfBirth",   label: "Date of Birth", type: "date" },
  { key: "aadhaar",       label: "Aadhaar" },
  { key: "casteCategory", label: "Caste Category" },
  { key: "religion",      label: "Religion" },
  { key: "nationality",   label: "Nationality" },
  { key: "motherTongue",  label: "Mother Tongue" },
];

const statusColors = {
  PENDING: "bg-yellow-100 text-yellow-800",
  FORM_SUBMITTED: "bg-blue-100 text-blue-800",
  PAYMENT_DONE: "bg-purple-100 text-purple-800",
  VERIFIED: "bg-green-100 text-green-800",
  ADMITTED: "bg-green-200 text-green-900",
  REJECTED: "bg-red-100 text-red-800",
};

const AdmissionList = () => {
  const [admissions, setAdmissions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [filters, setFilters] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    apiJson("/api/admin/master/academic-sessions").then(setSessions).catch(() => {});
    apiJson("/api/admin/master/degrees").then(setDegrees).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAdmissions();
  }, [filters]);

  const fetchAdmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.academicSessionId) params.set("academicSessionId", filters.academicSessionId);
      if (filters.degreeId) params.set("degreeId", filters.degreeId);
      if (filters.status) params.set("status", filters.status);
      if (filters.search) params.set("search", filters.search);
      const data = await apiJson(`/api/admin/admissions?${params}`);
      setAdmissions(data.admissions || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (id) => {
    try {
      const data = await apiJson(`/api/admin/admissions/${id}`);
      setSelectedAdmission(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleVerify = async (id) => {
    if (!window.confirm("Approve this admission?")) return;
    setError("");
    try {
      await apiJson(`/api/admin/admissions/${id}/verify`, { method: "POST" });
      setMessage("Admission approved!");
      setSelectedAdmission(null);
      fetchAdmissions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Rejection reason:");
    if (reason === null) return;
    setError("");
    try {
      await apiJson(`/api/admin/admissions/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      setMessage("Admission rejected.");
      setSelectedAdmission(null);
      fetchAdmissions();
    } catch (err) {
      setError(err.message);
    }
  };

  if (selectedAdmission) {
    return (
      <AdmissionDetail
        admission={selectedAdmission}
        onBack={() => setSelectedAdmission(null)}
        onRefresh={() => viewDetail(selectedAdmission.id)}
        onVerify={handleVerify}
        onReject={handleReject}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-4">Admissions ({total})</h1>

      {message && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 text-sm">{message}</div>}
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>}

      {/* Filters */}
      <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-3">
        <select
          value={filters.academicSessionId || ""}
          onChange={(e) => setFilters({ ...filters, academicSessionId: e.target.value })}
          className="px-2 py-1.5 border rounded text-sm"
        >
          <option value="">All Sessions</option>
          {sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filters.degreeId || ""}
          onChange={(e) => setFilters({ ...filters, degreeId: e.target.value })}
          className="px-2 py-1.5 border rounded text-sm"
        >
          <option value="">All Degrees</option>
          {degrees.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select
          value={filters.status || ""}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-2 py-1.5 border rounded text-sm"
        >
          <option value="">All Status</option>
          {["PENDING", "FORM_SUBMITTED", "PAYMENT_DONE", "ADMITTED", "REJECTED"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search name/reg no..."
          value={filters.search || ""}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="px-2 py-1.5 border rounded text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2">ID</th>
              <th className="text-left px-3 py-2">Student</th>
              <th className="text-left px-3 py-2">Reg No</th>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Degree</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admissions.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400">No admissions found</td></tr>
            ) : (
              admissions.map((a) => (
                <tr key={a.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2">{a.id}</td>
                  <td className="px-3 py-2">{a.student?.name}</td>
                  <td className="px-3 py-2">{a.student?.applyId}</td>
                  <td className="px-3 py-2">{a.academicSession?.name}</td>
                  <td className="px-3 py-2">{a.degree?.name}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${statusColors[a.status] || "bg-gray-100"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => viewDetail(a.id)} className="text-indigo-600 hover:underline text-xs">
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdmissionDetail = ({ admission, onBack, onRefresh, onVerify, onReject }) => {
  const a = admission;
  const student = a.student;
  const canVerify = ["PAYMENT_DONE", "FORM_SUBMITTED"].includes(a.status);

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack} className="text-indigo-600 hover:underline text-sm mb-4 inline-block">
        &larr; Back to list
      </button>

      <h1 className="text-xl font-bold text-gray-800 mb-4">Admission Detail #{a.id}</h1>

      <div className="space-y-4">
        {/* Status */}
        <div className="bg-white p-4 rounded shadow">
          <span className={`px-3 py-1 rounded text-sm ${statusColors[a.status] || "bg-gray-100"}`}>
            {a.status}
          </span>
          <span className="ml-3 text-sm text-gray-500">
            Session: {a.academicSession?.name} | Degree: {a.degree?.name}
          </span>
        </div>

        {/* Student Info + Edit */}
        <StudentInfoSection admission={a} onSaved={onRefresh} />

        {/* Addresses */}
        {student.addresses?.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-2">Addresses</h2>
            {student.addresses.map((addr, i) => (
              <div key={i} className="mb-2 text-sm">
                <strong>{addr.type}:</strong> {[addr.address, addr.district, addr.state, addr.pinCode].filter(Boolean).join(", ") || "Not provided"}
                {addr.mobileNo && ` | Mobile: ${addr.mobileNo}`}
              </div>
            ))}
          </div>
        )}

        {/* Qualifications */}
        {student.qualifications?.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-2">Academic Qualifications</h2>
            <div className="text-sm space-y-1">
              {student.qualifications.map((q, i) => (
                <p key={i}>
                  <strong>{q.level}:</strong> {q.boardName || "-"} | Roll: {q.classRollNo || "-"} | Year: {q.yearOfPassing || "-"} | %: {q.percentage || "-"}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Subject Selections */}
        {a.subjectSelections?.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-2">Subject Selections</h2>
            <div className="text-sm space-y-1">
              {a.subjectSelections.map((s, i) => (
                <p key={i}><strong>{s.subjectCategory?.name}:</strong> {s.selectedOption}</p>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {a.documentUploads?.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-2">Documents</h2>
            <div className="text-sm space-y-1">
              {a.documentUploads.map((d, i) => (
                <p key={i}>
                  <strong>{d.documentType?.name}:</strong>{" "}
                  <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                    View
                  </a>
                  {" "}(uploaded {new Date(d.uploadedAt).toLocaleDateString("en-IN")})
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Payments */}
        {a.payments?.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-2">Payments</h2>
            {a.payments.map((p, i) => (
              <div key={i} className="text-sm mb-2">
                <p>
                  <strong>Amount:</strong> {parseFloat(p.totalAmount).toFixed(2)} |{" "}
                  <strong>Status:</strong> {p.paymentStatus} |{" "}
                  <strong>Mode:</strong> {p.paymentMode} |{" "}
                  <strong>Txn:</strong> {p.transactionId || "N/A"}
                </p>
                {p.lineItems?.map((li, j) => (
                  <p key={j} className="ml-4 text-gray-500">
                    {li.feeHead.name}: {parseFloat(li.amount).toFixed(2)}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        {canVerify && (
          <div className="flex gap-3">
            <button
              onClick={() => onVerify(a.id)}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 text-sm"
            >
              Approve Admission
            </button>
            <button
              onClick={() => onReject(a.id)}
              className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 text-sm"
            >
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const StudentInfoSection = ({ admission, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [audits, setAudits] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const student = admission.student;

  const beginEdit = () => {
    const d = {};
    for (const f of EDIT_FIELDS) {
      let v = student[f.key];
      if (f.type === "date" && v) v = new Date(v).toISOString().split("T")[0];
      d[f.key] = v || "";
    }
    setDraft(d);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await apiJson(`/api/admin/admissions/${admission.id}/student`, {
        method: "PUT",
        body: JSON.stringify(draft),
      });
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const loadAudit = async () => {
    try {
      const data = await apiJson(`/api/admin/admissions/${admission.id}/student/audit`);
      setAudits(data);
      setShowAudit(true);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="bg-white p-4 rounded shadow">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-gray-700">Student Information</h2>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="bg-indigo-600 text-white text-xs px-3 py-1 rounded hover:bg-indigo-700">
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(false)} className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-300">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={beginEdit} className="bg-indigo-600 text-white text-xs px-3 py-1 rounded hover:bg-indigo-700">
                Edit
              </button>
              <button onClick={loadAudit} className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-300">
                Audit
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-xs">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
        <Info label="Apply ID" value={student.applyId} />
        {EDIT_FIELDS.map((f) => {
          if (!editing) {
            const v = student[f.key];
            const display = f.type === "date" && v ? new Date(v).toLocaleDateString("en-IN") : v;
            return <Info key={f.key} label={f.label} value={display} />;
          }
          const opts = SELECT_OPTS[f.key];
          return (
            <div key={f.key}>
              <p className="text-xs text-gray-500 mb-1">{f.label}</p>
              {opts ? (
                <select
                  value={draft[f.key] || ""}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  className="w-full px-2 py-1 border rounded text-xs"
                >
                  <option value="">--</option>
                  {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={f.type || "text"}
                  value={draft[f.key] || ""}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  className="w-full px-2 py-1 border rounded text-xs"
                />
              )}
            </div>
          );
        })}
      </div>

      {showAudit && (
        <div className="mt-3 border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Audit Trail</h3>
            <button onClick={() => setShowAudit(false)} className="text-xs text-gray-500 hover:underline">Hide</button>
          </div>
          {audits.length === 0 ? (
            <p className="text-xs text-gray-500">No changes recorded.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1">When</th>
                  <th className="text-left">By</th>
                  <th className="text-left">Field</th>
                  <th className="text-left">Old → New</th>
                </tr>
              </thead>
              <tbody>
                {audits.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="py-1">{new Date(a.changedAt).toLocaleString("en-IN")}</td>
                    <td>{a.changedByRole}{a.changedBy ? ` #${a.changedBy}` : ""}</td>
                    <td className="font-mono">{a.fieldKey}</td>
                    <td><span className="text-gray-500">{a.oldValue || "∅"}</span> → <span className="font-medium">{a.newValue || "∅"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-medium">{value || "-"}</p>
  </div>
);

export default AdmissionList;
